import { getDay } from 'owa-datetime';
import { minmax } from '../../minmax';

// This logic has been repurposed from the single Working hours behavior.
// This is to handle cases where the working hours are given in user's time zone and need to be converted to UTC
const timezoneCorrection = (
    workHoursStartTimeInMinutes,
    workHoursEndTimeInMinutes,
    date,
    workDays
) => {
    // If the start offset is absolutely bigger than 1 day (1440 minutes) then we need
    // to offset all our math. This happens when time zones are very far apart.
    // For example, when work hours start at midnight in UTC+14 and user time zone is UTC-12:
    // In this situation, Monday 00:00 show up on the previous Saturday at 10pm.
    // With this offset (-1560), the Saturday column needs to calculate things as if it was Sunday
    // so that it looks at "next day" being Monday. The reverse happens if the offset is positive.
    const dayOffset =
        (workHoursStartTimeInMinutes < 0 ? 1 : -1) *
        Math.floor(Math.abs(workHoursStartTimeInMinutes) / 1440);

    const s = workHoursStartTimeInMinutes + dayOffset * 1440;
    const e = workHoursEndTimeInMinutes + dayOffset * 1440;
    const thisDay = (7 + getDay(date) + dayOffset) % 7;
    const prevDay = (7 + thisDay - 1) % 7;
    const nextDay = (7 + thisDay + 1) % 7;
    const thisIsWorkingDay = workDays.includes(thisDay);
    const prevIsWorkingDay = workDays.includes(prevDay);
    const nextIsWorkingDay = workDays.includes(nextDay);

    // Normalize the start and end minutes so they are positive numbers.
    // Worst case is making start at midnight in UTC+14 and using TimeZone UTC-12
    // which causes WorkHoursStartTimeInMinutes to be -1560. So we add 2880
    // to ensure the resulting value will be positive.
    const whStart = (2880 + s) % 1440;
    const whEnd = (2880 + e) % 1440;

    // |    s<0    |    e<0    | next_day.start
    // |    s<0    | 0<=e<1440 | next_day.start
    // | 0<=s<1440 | 0<=e<1440 | this_day.start
    // | 0<=s<1440 |  e>=1440  | this_day.start
    // |  s>=1440  |  e>=1440  | prev_day.start
    const whStartOrEndOfDay =
        (s < 0 && nextIsWorkingDay) ||
        (0 <= s && s < 1440 && thisIsWorkingDay) ||
        (s >= 1440 && prevIsWorkingDay)
            ? whStart
            : 1440;

    // |    s<0    |    e<0    | next_day.end
    // |    s<0    | 0<=e<1440 | this_day.end
    // | 0<=s<1440 | 0<=e<1440 | this_day.end
    // | 0<=s<1440 |  e>=1440  | prev_day.end
    // |  s>=1440  |  e>=1440  | prev_day.end
    const whEndOrStartOfDay =
        (e < 0 && nextIsWorkingDay) ||
        (0 <= e && e < 1440 && thisIsWorkingDay) ||
        (e >= 1440 && prevIsWorkingDay)
            ? whEnd
            : 0;

    return { whStartOrEndOfDay, whEndOrStartOfDay, whStart, whEnd };
};

// This logic has been repurposed from the single Working hours behavior.
// This is to handle cases where there is only a single working hour block in the provided date
export const generateNonWorkingHoursBlock = (
    workHoursStartTimeInMinutes,
    workHoursEndTimeInMinutes,
    date,
    isPast,
    workDays,
    dayStartMinute,
    dayLength,
    isTimezoneCorrectionRequired
) => {
    // Clip the values to the visible range
    let clippedWhStart = minmax(0, workHoursStartTimeInMinutes - dayStartMinute, dayLength);
    let clippedWhEnd = minmax(0, workHoursEndTimeInMinutes - dayStartMinute, dayLength);

    // multiple variables are needed here as if timezone correction is needed, these values would change
    let workingHoursStartOrEndOfDay = workHoursStartTimeInMinutes;
    let workingHoursEndOrStartOfDay = workHoursEndTimeInMinutes;
    let workingHoursStart = workHoursStartTimeInMinutes;
    let workingHoursEnd = workHoursEndTimeInMinutes;

    // timezone correction required, then clipped values need to change
    if (isTimezoneCorrectionRequired) {
        const { whStartOrEndOfDay, whEndOrStartOfDay, whStart, whEnd } = timezoneCorrection(
            workHoursStartTimeInMinutes,
            workHoursEndTimeInMinutes,
            date,
            workDays
        );

        clippedWhStart = minmax(0, whStartOrEndOfDay - dayStartMinute, dayLength);
        clippedWhEnd = minmax(0, whEndOrStartOfDay - dayStartMinute, dayLength);
        workingHoursStartOrEndOfDay = whStartOrEndOfDay;
        workingHoursEndOrStartOfDay = whEndOrStartOfDay;
        workingHoursStart = whStart;
        workingHoursEnd = whEnd;
    }

    // Generate the clipped blocks of shadows we want to draw
    let nonWorkingTimes: { start: number; end: number }[] = [];

    if ((workingHoursStartOrEndOfDay == 1440 && workingHoursEndOrStartOfDay == 0) || isPast) {
        // If the start and end are reversed and at the edges of the day then
        // this is not a working day and it is not showing working hours
        // from the previous or next day either, so we shadow the entire day.
        nonWorkingTimes.push({ start: 0, end: dayLength });
    } else if (workingHoursStart < workingHoursEnd) {
        // If the start and end times are in order then we have a block
        // of working hours in the middle of the day and we might have two
        // blocks of shadow, one before and one after the working hours,
        // unless they end up being clipped by the day start and day length settings.
        if (clippedWhStart) {
            nonWorkingTimes.push({ start: 0, end: clippedWhStart });
        }

        if (clippedWhEnd < dayLength) {
            nonWorkingTimes.push({ start: clippedWhEnd, end: dayLength });
        }
    } else if (workingHoursEnd < workingHoursStart) {
        // If the start and end times are reversed, then the time zones
        // are causing the working hours to happen across midnight so
        // we have a single block of shadow in the middle of the day.
        // The clipped values already account for the fact that previous
        // and next day might not be work days.
        nonWorkingTimes.push({ start: clippedWhEnd, end: clippedWhStart });
    }

    return nonWorkingTimes;
};
